import React, { useState, useEffect } from 'react';
import { 
  Car, 
  MapPin, 
  Calendar, 
  Clock, 
  Users, 
  DollarSign, 
  Star, 
  Search, 
  Filter,
  Phone,
  MessageCircle,
  Heart,
  Share2,
  Navigation
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { fetchWithAuth } from '../services/fetchWithAuth';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';

const Announcements = () => {
  const { logout, user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [filteredAnnouncements, setFilteredAnnouncements] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [tab, setTab] = useState('all');
  const navigate = useNavigate();
  const location = useLocation();

  const dummyAnnouncements = [
    {
      id: 1,
      user: {
        name: 'Priya Sharma',
        rating: 4.8,
        rides: 45,
        avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face'
      },
      from: 'Mumbai Central',
      to: 'Andheri West',
      date: '2024-01-15',
      time: '09:00 AM',
      price: 120,
      seats: 2,
      description: 'Regular office commute. Prefer non-smokers. Will share fuel cost.',
      vehicle: 'Honda City',
      status: 'active',
      distance: '12 km',
      duration: '45 min'
    },
    {
      id: 2,
      user: {
        name: 'Rahul Mehta',
        rating: 4.6,
        rides: 32,
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face'
      },
      from: 'Bandra West',
      to: 'Powai',
      date: '2024-01-15',
      time: '08:30 AM',
      price: 150,
      seats: 1,
      description: 'Early morning ride. Music lovers welcome!',
      vehicle: 'Maruti Swift',
      status: 'active',
      distance: '18 km',
      duration: '55 min'
    },
    {
      id: 3,
      user: {
        name: 'Anjali Patel',
        rating: 4.9,
        rides: 67,
        avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face'
      },
      from: 'Thane',
      to: 'BKC',
      date: '2024-01-16',
      time: '07:45 AM',
      price: 200,
      seats: 3,
      description: 'Corporate ride. Professional environment preferred.',
      vehicle: 'Hyundai i20',
      status: 'active',
      distance: '25 km',
      duration: '75 min'
    },
    {
      id: 4,
      user: {
        name: 'Vikram Singh',
        rating: 4.7,
        rides: 28,
        avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
      },
      from: 'Navi Mumbai',
      to: 'Fort',
      date: '2024-01-15',
      time: '10:00 AM',
      price: 180,
      seats: 2,
      description: 'Weekend shopping trip. Flexible timing.',
      vehicle: 'Tata Nexon',
      status: 'active',
      distance: '30 km',
      duration: '90 min'
    },
    {
      id: 5,
      user: {
        name: 'Sneha Reddy',
        rating: 4.5,
        rides: 19,
        avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face'
      },
      from: 'Kurla',
      to: 'Worli',
      date: '2024-01-16',
      time: '08:15 AM',
      price: 100,
      seats: 1,
      description: 'Student-friendly ride. Affordable pricing.',
      vehicle: 'Maruti Alto',
      status: 'active',
      distance: '15 km',
      duration: '50 min'
    }
  ];

  const [userAnnouncements, setUserAnnouncements] = useState([]);

  // Move fetchAnnouncements outside so it can be reused
    const fetchAnnouncements = async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth('/api/announcements', {}, logout);
        if (!response.ok) throw new Error('Failed to fetch announcements');
        const data = await response.json();
        const mapped = (data.announcements || data).map(a => ({
          id: a.id || a._id,
          user: {
            name: a.user?.name || a.userId?.name || 'Unknown',
            rating: a.user?.rating || a.userId?.rating || 4.5,
            rides: a.user?.rides || a.userId?.rides || 0,
            avatar: a.user?.profilePicture || a.userId?.profilePicture || 'https://via.placeholder.com/150',
            id: a.user?.id || a.userId?._id || a.userId || ''
          },
          from: a.sourceLocation?.address || '',
          to: a.destination || '',
          date: a.rideTime ? a.rideTime.slice(0,10) : '',
          time: a.rideTime ? new Date(a.rideTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '',
          price: a.connectCost || 0,
          seats: a.maxPassengers || 1,
          description: a.description || '',
          vehicle: a.vehicleInfo?.model || '',
          status: a.status || 'active',
          distance: a.distance || '',
          duration: a.duration || ''
        }));
        setAnnouncements(mapped);
        setFilteredAnnouncements(mapped);
        // Filter for current user's announcements
        if (user && user.id) {
          setUserAnnouncements(mapped.filter(a => a.user.id === user.id));
        } else {
          setUserAnnouncements([]);
        }
      } catch (err) {
        setAnnouncements([]);
        setFilteredAnnouncements([]);
        setUserAnnouncements([]);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    const shouldRefresh = localStorage.getItem('refreshAnnouncements');
    if (shouldRefresh) {
      localStorage.removeItem('refreshAnnouncements');
      fetchAnnouncements();
    }
  }, [user, location]);

  useEffect(() => {
    fetchAnnouncements();
  }, [user]);

  useEffect(() => {
    let filtered = announcements;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(announcement =>
        announcement.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
        announcement.to.toLowerCase().includes(searchTerm.toLowerCase()) ||
        announcement.user.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply category filter
    if (selectedFilter !== 'all') {
      switch (selectedFilter) {
        case 'today':
          filtered = filtered.filter(announcement => 
            announcement.date === '2024-01-15'
          );
          break;
        case 'tomorrow':
          filtered = filtered.filter(announcement => 
            announcement.date === '2024-01-16'
          );
          break;
        case 'cheap':
          filtered = filtered.filter(announcement => 
            announcement.price <= 150
          );
          break;
        case 'premium':
          filtered = filtered.filter(announcement => 
            announcement.price > 150
          );
          break;
        default:
          break;
      }
    }

    setFilteredAnnouncements(filtered);
  }, [searchTerm, selectedFilter, announcements]);

  const handleFavorite = (announcementId) => {
    setFavorites(prev => 
      prev.includes(announcementId) 
        ? prev.filter(id => id !== announcementId)
        : [...prev, announcementId]
    );
  };

  const handleConnect = (announcement) => {
    alert(`Connecting with ${announcement.user.name} for ride from ${announcement.from} to ${announcement.to}`);
  };

  const handleCall = (announcement) => {
    alert(`Calling ${announcement.user.name} at +91-98765-43210`);
  };

  const handleMessage = (announcement) => {
    alert(`Opening chat with ${announcement.user.name}`);
  };

  const handleShare = (announcement) => {
    const shareText = `Check out this ride: ${announcement.from} to ${announcement.to} on ${announcement.date} at ${announcement.time}`;
    if (navigator.share) {
      navigator.share({
        title: 'LinkCab Ride',
        text: shareText,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(shareText);
      alert('Ride details copied to clipboard!');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Add announcement to ride history in backend
  const addAnnouncementToRideHistory = async (announcement) => {
    const ride = {
      id: `announcement-${announcement.id}`,
      source: announcement.from,
      destination: announcement.to,
      rideTime: `${announcement.date} ${announcement.time}`,
      fare: announcement.price,
      partnerName: announcement.user.name,
      partnerRating: announcement.user.rating,
      userRating: null,
      status: 'completed',
      rideDate: new Date().toISOString(),
      feedback: '',
      aiRating: null,
      aiConfidence: null,
    };
    try {
      const response = await fetchWithAuth('/api/users/ride-history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(ride),
      }, logout);
      if (response.ok) {
        toast.success('Added to ride history!');
        setTimeout(() => navigate('/ride-history'), 500);
      } else if (response.status === 409) {
        toast('Already in ride history');
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to add ride to history');
      }
    } catch (e) {
      toast.error('Failed to add ride to history');
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading rides...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Find Rides</h1>
        <p className="text-gray-600">Discover and connect with fellow commuters</p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by location or driver name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2">
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Rides</option>
              <option value="today">Today</option>
              <option value="tomorrow">Tomorrow</option>
              <option value="cheap">Under ₹150</option>
              <option value="premium">Premium</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-6">
        <p className="text-gray-600">
          Found {filteredAnnouncements.length} ride{filteredAnnouncements.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Tab Switcher */}
      <div className="mb-8">
        <div className="flex space-x-4">
          <button onClick={() => setTab('all')} className={`px-4 py-2 rounded ${tab === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}>All Rides</button>
          <button onClick={() => setTab('mine')} className={`px-4 py-2 rounded ${tab === 'mine' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}>My Announcements</button>
        </div>
      </div>

      {/* Add Announcement Button for My Announcements Tab */}
      {tab === 'mine' && (
        <div className="mb-6 flex justify-end">
          <button
            onClick={() => navigate('/create-announcement')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold shadow hover:bg-blue-700 transition-colors"
          >
            + Add Announcement
          </button>
        </div>
      )}

      {/* Announcements Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {(tab === 'all' ? [...dummyAnnouncements, ...filteredAnnouncements] : userAnnouncements).map((announcement) => (
          <div key={announcement.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer" onClick={() => addAnnouncementToRideHistory(announcement)}>
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img
                    src={announcement.user.avatar}
                    alt={announcement.user.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">{announcement.user.name}</h3>
                    <div className="flex items-center space-x-2">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-gray-600">{announcement.user.rating}</span>
                      <span className="text-sm text-gray-500">({announcement.user.rides} rides)</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handleFavorite(announcement.id)}
                  className={`p-2 rounded-full transition-colors ${
                    favorites.includes(announcement.id)
                      ? 'text-red-500 bg-red-50'
                      : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${favorites.includes(announcement.id) ? 'fill-current' : ''}`} />
                </button>
              </div>

              {/* Route */}
              <div className="flex items-center space-x-3 mb-3">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-green-500" />
                    <span className="font-medium">{announcement.from}</span>
                  </div>
                  <div className="flex items-center space-x-2 mt-1">
                    <Navigation className="w-4 h-4 text-red-500" />
                    <span className="font-medium">{announcement.to}</span>
                  </div>
                </div>
                {/* Price removed as per request */}
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span>{announcement.date}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span>{announcement.time}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span>{announcement.seats} seat{announcement.seats !== 1 ? 's' : ''} available</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Car className="w-4 h-4 text-gray-400" />
                  <span>{announcement.vehicle}</span>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="p-6 border-b border-gray-100">
              <p className="text-gray-600 text-sm">{announcement.description}</p>
            </div>

            {/* Actions */}
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleConnect(announcement)}
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Connect
                  </button>
                  <button
                    onClick={() => handleCall(announcement)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Call"
                  >
                    <Phone className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleMessage(announcement)}
                    className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Message"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleShare(announcement)}
                    className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Share"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredAnnouncements.length === 0 && (
        <div className="text-center py-12">
          <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No rides found</h3>
          <p className="text-gray-600">Try adjusting your search criteria or check back later.</p>
        </div>
      )}
    </div>
  );
};

export default Announcements; 